import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { StatusReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/status/main.tsx';
import {
	getStatusValue,
	useStatusValue,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/status/index.tsx';
import { useObjectSchemaId } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { StatusAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { StatusPicker } from '@atlassian/jira-servicedesk-insight-status-picker/src/ui/index.tsx';
import { EditViewSpacers } from '../../common/ui/edit-view-spacers/styled.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: StatusAttributeType;
};

const EditableStatusAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;
	const [objectSchemaId] = useObjectSchemaId();
	const [{ attribute }] = useEditableAttribute(attributeType);

	const { typeValueMulti } = attributeType;
	const [statusValue, setStatusValue] = useStatusValue(attribute);

	const resetValue = useCallback(() => {
		setStatusValue(getStatusValue(attribute));
	}, [attribute, setStatusValue]);

	const statusForReadView = statusValue
		? { id: statusValue.value, name: statusValue.label, category: statusValue.category }
		: null;

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.status-attribute"
			// we keep the edit view mode open by default
			// this is to align with designs that break away from the regular inline edit pattern and display a custom status picker
			isEditingByDefault={false}
			attributeType={attributeType}
			isEditable={isEditable}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<StatusReadView status={statusForReadView} />
				</ObjectDetailsSpacer>
			)}
			readViewFitContainerWidth
			renderEditView={({ onConfirmChange, hasValidationError }) =>
				objectSchemaId !== undefined ? (
					<EditViewSpacers.StatusPicker>
						<StatusPicker
							menuIsOpen
							autoFocus
							value={statusValue}
							onChange={(statusOptions) => {
								const [statusOption] = statusOptions;
								onConfirmChange?.(statusOption.value);
								setStatusValue(statusOption);
							}}
							isEditable={isEditable}
							objectSchemaId={objectSchemaId}
							objectTypeMultiValues={typeValueMulti}
							hasValidationError={hasValidationError}
						/>
					</EditViewSpacers.StatusPicker>
				) : (
					<></>
				)
			}
			onResetValue={resetValue}
		/>
	);
};

export const StatusEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightEditableStatusAttribute"
		packageName="jiraServicedeskInsightEditableStatusAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<EditableStatusAttribute {...props} />
	</JSErrorBoundary>
);
